var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { forwardRef, useCallback } from 'react';
import { Listbox } from '@headlessui/react';
import getSelectSize from './private/utils/getSelectSize';
import SelectButtonContext from './private/utils/SelectButtonContext';
import useSelectButtonContext from './private/utils/useSelectButtonContext';
import mergeClassnames from '../mergeClassnames/mergeClassnames';
import ControlsChevronDownSmall from '../private/icons/ControlsChevronDownSmall';
import ControlsCloseSmall from '../private/icons/ControlsCloseSmall';
import Tag from '../tag/Tag';
const SelectButtonRoot = forwardRef((_a, ref) => {
    var { label, placeholder, size = 'md', isError, idDisabled, open, children, isUppercase } = _a, rest = __rest(_a, ["label", "placeholder", "size", "isError", "idDisabled", "open", "children", "isUppercase"]);
    const states = Object.assign({ open: open, size: size, isError: isError, idDisabled: idDisabled }, rest);
    return (React.createElement("div", { className: "relative", ref: ref },
        React.createElement(SelectButtonContext.Provider, { value: states }, children)));
});
const Input = ({ children, className }) => {
    const _a = useSelectButtonContext('SelectButton.Input'), { size, isError, idDisabled } = _a, rest = __rest(_a, ["size", "isError", "idDisabled"]);
    return (React.createElement("button", Object.assign({}, rest, { className: mergeClassnames('flex items-center justify-between gap-2', 'w-full bg-goku border-beerus', 'shadow-input hover:shadow-input-hov transition-shadow duration-200 ', 'focus:shadow-input-focus focus:outline-none', getSelectSize(size), isError &&
            'shadow-input-err hover:shadow-input-err focus:shadow-input-err', idDisabled && 'opacity-60 cursor-not-allowed hover:shadow-input', className && className) }),
        React.createElement("span", { className: "flex w-full flex-col items-start text-start overflow-hidden" }, children),
        React.createElement(Control, null)));
};
const InsetInput = ({ children, className }) => {
    const _a = useSelectButtonContext('SelectButton.Input'), { isError, idDisabled } = _a, rest = __rest(_a, ["isError", "idDisabled"]);
    return (React.createElement("button", Object.assign({}, rest, { className: mergeClassnames('flex items-center justify-between', 'w-full bg-goku border-beerus', 'shadow-input hover:shadow-input-hov transition-shadow duration-200 ', 'focus:shadow-input-focus focus:outline-none', 'h-14 py-2 px-4 rounded-moon-i-md', isError &&
            'shadow-input-err hover:shadow-input-err focus:shadow-input-err', idDisabled && 'opacity-60 cursor-not-allowed hover:shadow-input', className && className) }),
        React.createElement("span", { className: "flex gap-2 items-center overflow-hidden" }, children),
        React.createElement(Control, null)));
};
const Value = ({ children }) => {
    const { size } = useSelectButtonContext('SelectButton.Value');
    return (React.createElement("span", { className: mergeClassnames(size === 'sm' ? 'text-moon-14' : 'text-moon-16', 'w-full text-bulma truncate') }, children));
};
const Label = ({ children, labelSize, idDisabled }) => {
    const { size } = useSelectButtonContext('SelectButton.Label');
    const currentSize = labelSize || size;
    return (React.createElement(Listbox.Label, { className: mergeClassnames('block text-bulma pb-2', currentSize === 'sm' ? 'text-moon-14' : 'text-moon-16', idDisabled && 'opacity-60 cursor-not-allowed') }, children));
};
const FloatingLabel = ({ children }) => (React.createElement(Listbox.Label, { className: "w-full text-moon-12 text-trunks text-start truncate" }, children));
const Placeholder = ({ children }) => {
    const { size } = useSelectButtonContext('SelectButton.Placeholder');
    return (React.createElement("span", { className: mergeClassnames(size === 'sm' ? 'text-moon-14' : 'text-moon-16', 'w-full text-trunks text-start truncate') }, children));
};
const Control = () => {
    const { open, size } = useSelectButtonContext('SelectButton.Control');
    return (React.createElement(ControlsChevronDownSmall, { className: mergeClassnames('text-trunks flex-shrink-0 transition-transform', size === 'sm' ? 'text-moon-16' : 'text-moon-24', open && 'rotate-[-180deg]') }));
};
const Chip = (_a) => {
    var { children, onClear, isUppercase } = _a, rest = __rest(_a, ["children", "onClear", "isUppercase"]);
    const { size } = useSelectButtonContext('SelectButton.Control');
    const clickHandler = useCallback((e) => {
        e.preventDefault();
        onClear && onClear();
    }, [onClear]);
    return (React.createElement(Tag, Object.assign({ size: size === 'sm' ? '2xs' : 'xs', iconRight: React.createElement(ControlsCloseSmall, { onClick: clickHandler, className: "cursor-pointer" }), isUppercase: isUppercase }, rest), children));
};
const SelectButton = Object.assign(SelectButtonRoot, {
    Label,
    FloatingLabel,
    Placeholder,
    Control,
    Input,
    Value,
    Chip,
    InsetInput,
});
export default SelectButton;
